// 입력 수집 (§4): 쓰는 동안 몰래 모은다. 화면엔 안 띄운다.
// 기기 무관 신호 중심(길이·삭제율·문장부호·리듬). 타속은 보조.
//
// onChange 기반 diff로 추적한다 — 한글 IME·붙여넣기·자동완성을 keydown보다
// 정확히 잡는다(모바일/한국어 핵심). 타임스탬프는 변경 이벤트 단위로 기록.

import type { InputMode, RawSignals } from "./types";
import type { VoiceCapture } from "./voiceMetrics";

// 발화속도 정규화 기준(글자/초). 한국어 음절 기준 ~12자/초면 매우 빠름 → 1. (튜닝)
const RATE_REF = 12;
function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

const KO_FILLERS = ["진짜", "약간", "그냥", "ㅋㅋ", "음", "어", "그러니까"];
const EN_FILLERS = ["like", "um", "uh", "so", "literally", "lol"];

export class InputTracker {
  private startedAt: number | null = null; // 첫 입력 시각
  private mountedAt: number; // 화면 진입 시각(머뭇거림 측정)
  private events: number[] = []; // 변경 이벤트 타임스탬프(작성 시작 기준)
  private lenHist: Array<{ len: number; t: number }> = []; // 길이 추이(삽입/삭제는 '안정 길이' 증감으로 산출)

  constructor(private now: () => number = () => performance.now()) {
    this.mountedAt = this.now();
  }

  /** textarea가 포커스되면(=사용자가 입력 준비) 머뭇거림 기준 시각을 리셋한다. 첫 입력 전에만.
   *  화면을 띄워두고 한참 뒤 입력하거나(테스트·앱 백그라운드) 해서 startDelay가 부풀던 문제 방지. */
  markReady(): void {
    if (this.startedAt === null) this.mountedAt = this.now();
  }

  /** textarea onChange에서 호출. 길이+타임스탬프만 기록한다. 삭제/삽입 산출은 finalize에서.
   *  ★토스 웹뷰의 한글 IME는 조합 중에도 진짜 deleteContentBackward/길이 출렁임을 쏘므로(ㄱ→가→각을
   *    삭제+재삽입으로 처리) inputType·isComposing·composition이벤트 어느 신호도 못 믿는다(47→23→4로 다 샘).
   *    그래서 이벤트 신호를 안 쓰고, '안정 길이'(멈춤 직전 길이)의 증감으로만 센다(editCounts). */
  onChange(nextText: string): void {
    const t = this.now();
    if (this.startedAt === null) this.startedAt = t;
    this.events.push(t - this.startedAt);
    this.lenHist.push({ len: nextText.length, t });
  }

  hasInput(): boolean {
    return this.startedAt !== null;
  }

  /** ★삭제/삽입 = '안정 길이'들의 증감. 조합 churn(같은 키의 삭제+재삽입)은 GAP보다 짧게 붙어 한 묶음으로
   *  뭉쳐지므로 순간적 출렁임이 사라지고, 멈췄다 지운 '진짜 백스페이스'만 안정 길이 감소로 잡힌다. */
  private editCounts(finalLen: number): { inserts: number; deletes: number } {
    const GAP = 250; // ms — 이보다 길게 멈추면 그 직전 길이를 '확정'으로 본다(조합 churn은 즉각적이라 안 끊김)
    const settled: number[] = [];
    const h = this.lenHist;
    for (let i = 0; i < h.length; i++) {
      const gapAfter = i === h.length - 1 ? Infinity : h[i + 1].t - h[i].t;
      if (gapAfter >= GAP) settled.push(h[i].len);
    }
    if (settled.length === 0 || settled[settled.length - 1] !== finalLen) settled.push(finalLen);
    let inserts = 0;
    let deletes = 0;
    let prev = 0;
    for (const len of settled) {
      if (len > prev) inserts += len - prev;
      else if (len < prev) deletes += prev - len;
      prev = len;
    }
    return { inserts, deletes };
  }

  /** 제출 시 최종 텍스트로 RawSignals 완성. voice가 있으면 음성 지표를 붙인다(§4). */
  finalize(text: string, mode: InputMode = "text", voice?: VoiceCapture | null): RawSignals {
    const t = this.now();
    const durationMs = this.startedAt !== null ? t - this.startedAt : 0;
    const startDelayMs = this.startedAt !== null ? this.startedAt - this.mountedAt : 0;

    // 삭제/삽입은 '안정 길이' 증감으로 산출(IME 조합 churn 무시 — 가짜 백스페이스 방지).
    const { inserts, deletes } = this.editCounts(text.length);

    const exclaimCount = (text.match(/!/g) ?? []).length;
    const questionCount = (text.match(/\?/g) ?? []).length;
    const capsRun = (text.match(/[A-Z]{2,}|[ㄱ-ㅎ]{2,}/g) ?? []).reduce(
      (a, m) => a + m.length,
      0,
    );
    const repeatRun = (text.match(/(.)\1{2,}/g) ?? []).reduce((a, m) => a + m.length, 0);
    const fillerCount = [...KO_FILLERS, ...EN_FILLERS].reduce(
      (a, f) => a + (text.split(f).length - 1),
      0,
    );

    // 음성 지표(있을 때만). 발화속도 = 글자수 / 발화시간(초)을 정규화.
    const voiceData = voice
      ? {
          peakVolume: voice.peakVolume,
          pitchVariance: voice.pitchVariance,
          speechRate:
            voice.durationMs > 0
              ? clamp01(text.length / (voice.durationMs / 1000) / RATE_REF)
              : 0,
          volumeTimeline: voice.volumeTimeline,
          pauseRatio: voice.pauseRatio,
          pauseCount: voice.pauseCount,
        }
      : undefined;

    return {
      mode,
      durationMs,
      startDelayMs,
      charCount: text.length,
      insertCount: inserts,
      deleteCount: deletes,
      exclaimCount,
      questionCount,
      capsRun,
      repeatRun,
      fillerCount,
      eventTimestamps: [...this.events],
      voice: voiceData,
      text,
    };
  }
}
